/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Drew Burnett
 */

// Imports

import React, { useEffect } from "react"
import {
    DashboardPanelLink,
    DashboardPanel,
    Element,
    GamesList,
    Icon,
    Label,
    Loading,
    RelationsList,
    useToken,
    Color,
} from "trux"
import Masonry from "react-masonry-css"
import { useHistory } from "react-router"

import {
    useGetUserQuery,
    useGetUserRelationsQuery,
} from "../../generated/graphql"
import { traceIdUrl } from "../../util/links"
import * as act from "../Navigation/actions"
import { useDispatch, useSelector } from "react-redux"
import { Page } from "../Navigation"
import "./StartPage.css"

// Helpers

const greet = (user) => {
    let result = ""
    const firstName = user?.first_name
    if (firstName) {
        const today = new Date()
        const curHr = today.getHours()
        if (curHr < 12) {
            result = "Good morning " + firstName
        } else if (curHr < 18) {
            result = "Good afternoon " + firstName
        } else {
            result = "Good evening " + firstName
        }
    }
    return result
}

// Start Page

const StartPage = (props) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const acl = useSelector((state) => state.acl.value)
    const userDivisions = useSelector((state) => state.divisions.value)

    // Get the token
    const token = useToken("StartPage")
    const tokenReady = token !== null

    // Get the users acl to find trace id base url
    const opts = acl ? acl : {}
    const relationBaseURL = traceIdUrl("", opts)

    // use effect to redirect to login if token ready and empty
    useEffect(() => {
        // If token is ready and there isn't a token
        if (tokenReady && token.token === "") {
            // Redirect to login
            history.push("/magic/session/login")
        }
    }, [tokenReady, token, history])

    const handleLogOut = () => {
        dispatch(act.logoutUser())
        history.push("/magic/session/login")
    }

    // Get the user
    const { data: userData, loading: userLoading } = useGetUserQuery({
        variables: {
            user_id: token?.user_id,
            token: token,
        },
        skip: !tokenReady,
    })
    const user = userData ? userData.user : null
    const recentGames = user ? user.recent_games : null

    const totalTeamsCount = userDivisions
        ? userDivisions.reduce(
              (acc, division) => acc + division.myTeams.length,
              0
          )
        : 0
    const shouldShowDivisionsSeparately = userDivisions
        ? totalTeamsCount > 3
        : false
    const allTeams = userDivisions
        ? userDivisions.map((division) => division.myTeams).flat()
        : []

    // Get relations
    const { data: userRelations, loading: relationsLoading } =
        useGetUserRelationsQuery({
            variables: {
                to_user_id: token?.user_id,
                token: token,
                include_self: true,
            },
            skip: !tokenReady,
        })
    const relations = userRelations ? userRelations.userRelations : null

    const isLoading = !tokenReady || userLoading || relationsLoading
    const transparentClassName = !isLoading ? "" : "transparent"

    const sortedDivisions = [...(userDivisions ? userDivisions : [])].sort(
        (a, b) => a.title.localeCompare(b.title)
    )

    return (
        <Element trkContext="Start Page" className="StartPage">
            <Page>
                {!isLoading && (
                    <Label type={Label.Type.Heading1} semi center padding="8 4">
                        {greet(user)}
                    </Label>
                )}
                {isLoading && <Loading />}
                <Element className="DashboardContainer">
                    <Masonry
                        breakpointCols={{ default: 2, 766: 1 }}
                        className={`Dashboard ${transparentClassName}`}
                        columnClassName="grid-column"
                    >
                        <DashboardPanel title="Explore" containerized>
                            <DashboardPanelLink
                                trkAction="My Trace iD Pressed"
                                icon={Icon.Type.Moment}
                                href={`${relationBaseURL}/find/user/${user?.user_id}`}
                                title="My Trace iD"
                            />
                            {!shouldShowDivisionsSeparately &&
                                allTeams?.slice(0, 3).map((team) => {
                                    const divisionContentAccess =
                                        team && team.division
                                            ? team.division.content_access
                                            : ""
                                    const hasChurned = [
                                        "auto_denied",
                                        "manual_denied",
                                    ].includes(divisionContentAccess)
                                    const teamTitle = hasChurned
                                        ? `${team.title} subscription expired`
                                        : team.title
                                    const teamIcon = hasChurned
                                        ? Icon.Type.Lock
                                        : Icon.Type.Soccer
                                    const teamColor = hasChurned
                                        ? Color.Black
                                        : Color.Trace
                                    return (
                                        <DashboardPanelLink
                                            trkAction="Team Link Pressed"
                                            key={`startpage-divisions-${team.name}`}
                                            icon={teamIcon}
                                            to={`/${team.name}`}
                                            title={teamTitle}
                                            color={teamColor}
                                        />
                                    )
                                })}
                            <DashboardPanelLink
                                trkAction="Leaderboard Pressed"
                                icon={Icon.Type.Leaderboard}
                                href={`${process.env.REACT_APP_GO_TRACEUP}/leader/boards/`}
                                title="Leaderboards"
                            />
                        </DashboardPanel>
                        <DashboardPanel title="My family" containerized>
                            <RelationsList
                                baseURL={relationBaseURL}
                                relations={relations}
                            />
                        </DashboardPanel>
                        <DashboardPanel title="Recent games" containerized>
                            <GamesList games={recentGames} />
                        </DashboardPanel>
                        <DashboardPanel title="More" containerized>
                            <DashboardPanelLink
                                trkAction="Academy Pressed"
                                icon={Icon.Type.Academy}
                                href="https://traceup.com/soccer/academy"
                                title="Academy"
                            />
                            <DashboardPanelLink
                                trkAction="Refer a Team Pressed"
                                icon={Icon.Type.Referral}
                                href={`https://traceup.com/refer?utm_source=go&utm_medium=webapp&utm_campaign=refs&referred_by=${user?.email}`}
                                title="Refer a team. Get $100"
                            />
                            <DashboardPanelLink
                                trkAction="Help Pressed"
                                icon={Icon.Type.Question}
                                href="https://traceup.com/soccer/help"
                                title="Help"
                            />
                            <DashboardPanelLink
                                trkAction="Sign Out Pressed"
                                handleClick={(e) => handleLogOut()}
                                icon={Icon.Type.SignOut}
                                title="Sign out"
                            />
                        </DashboardPanel>
                        {shouldShowDivisionsSeparately &&
                            sortedDivisions.map((division) => {
                                const sortedDivisionTeams = [
                                    ...(division.teams ? division.teams : []),
                                ]
                                    .sort((a, b) =>
                                        a.title.localeCompare(b.title)
                                    )
                                    .sort((a, b) => {
                                        const aIsChurned = [
                                            "auto_denied",
                                            "manual_denied",
                                        ].includes(a.division?.content_access)
                                        const bIsChurned = [
                                            "auto_denied",
                                            "manual_denied",
                                        ].includes(b.division?.content_access)
                                        if (aIsChurned && !bIsChurned) return 1
                                        if (bIsChurned && !aIsChurned) return -1
                                        return 0
                                    })

                                return (
                                    <DashboardPanel
                                        key={`startpage-division-${division.title}`}
                                        title={division.title}
                                        headerLinkTitle={
                                            division.is_member ? "Settings" : ""
                                        }
                                        headerLink={
                                            division.is_member
                                                ? `${process.env.REACT_APP_GO_TRACEUP}/#/${division.teams[0].name}/settings/division/${division.division_id}`
                                                : null
                                        }
                                        containerized
                                    >
                                        {sortedDivisionTeams?.map((team) => {
                                            const divisionContentAccess =
                                                team && team.division
                                                    ? team.division
                                                          .content_access
                                                    : ""
                                            const hasChurned = [
                                                "auto_denied",
                                                "manual_denied",
                                            ].includes(divisionContentAccess)
                                            const teamTitle = hasChurned
                                                ? `${team.title} subscription expired`
                                                : team.title
                                            const teamColor = hasChurned
                                                ? Color.Black
                                                : Color.Trace
                                            const teamIcon = hasChurned
                                                ? Icon.Type.Lock
                                                : null
                                            return (
                                                <DashboardPanelLink
                                                    trkAction="Team Link Pressed"
                                                    key={`startpage-divisions-team-${team.name}`}
                                                    icon={teamIcon}
                                                    to={`/${team.name}`}
                                                    title={teamTitle}
                                                    color={teamColor}
                                                />
                                            )
                                        })}
                                    </DashboardPanel>
                                )
                            })}
                    </Masonry>
                </Element>
            </Page>
        </Element>
    )
}

export default StartPage
